// For SubmissionViewer.js in /src/components/common/
import React, { useState, useEffect } from 'react';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import PdfViewer from './PdfViewer';
import '../../styles/assignmentView.css';
import '../../styles/PdfViewer.css';
import '../../styles/SubmissionViewer.css';

// Fetches a fresh download URL from Storage to avoid expired token 403s
const getFreshUrl = async (storedUrl) => {
  try {
    const storage = getStorage();
    const url = new URL(storedUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)/);
    if (!pathMatch) return storedUrl;
    const filePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, filePath);
    return await getDownloadURL(fileRef);
  } catch (e) {
    console.error('Failed to refresh URL:', e);
    return storedUrl;
  }
};

const SubmissionViewer = ({ submission }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [freshUrls, setFreshUrls] = useState({});

  useEffect(() => {
    if (submission?.fileUrls && submission.fileUrls.length > 0) {
      setSelectedFile(submission.fileUrls[0]);

      // Fetch fresh URLs for all files
      const fetchAll = async () => {
        const entries = await Promise.all(
          submission.fileUrls.map(async (file) => {
            const fresh = await getFreshUrl(file.url);
            return [file.url, fresh];
          })
        );
        setFreshUrls(Object.fromEntries(entries));
      };
      fetchAll();
    } else {
      setSelectedFile(null);
      setFreshUrls({});
    }
  }, [submission]);

  if (!submission) {
    return <div className="no-submission">No submission available</div>;
  }

  const renderFileContent = (file) => {
    if (!file) return null;

    // Use fresh URL if available, fall back to stored URL
    const fileUrl = freshUrls[file.url] || file.url;

    const isPdf =
      file.type === 'application/pdf' ||
      file.name?.toLowerCase().endsWith('.pdf') ||
      file.url?.toLowerCase().includes('.pdf');

    if (isPdf) {
      return (
        <div className="file-preview-container">
          <h4>PDF Preview: {file.name}</h4>
          {fileUrl ? (
            <>
              <PdfViewer
                key={fileUrl}
                fileUrl={fileUrl}
                width={window.innerWidth > 768 ? 600 : 300}
              />
              <a
                href={fileUrl}
                download={file.name}
                className="download-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Download PDF
              </a>
            </>
          ) : (
            <div>Loading PDF...</div>
          )}
        </div>
      );
    } else if (file.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(file.name)) {
      return (
        <div className="file-preview-container">
          <h4>Image Preview: {file.name}</h4>
          <img
            src={fileUrl}
            alt={file.name}
            className="file-preview-image"
          />
          <a
            href={fileUrl}
            download={file.name}
            className="download-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Image
          </a>
        </div>
      );
    } else {
      return (
        <div className="file-preview-container">
          <h4>{file.name}</h4>
          <div className="file-type-icon">📄</div>
          <p>This file type cannot be previewed directly.</p>
          <a
            href={fileUrl}
            download={file.name}
            className="download-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download File
          </a>
        </div>
      );
    }
  };

  return (
    <div className="submission-viewer">
      {submission.content && (
        <div className="text-submission">
          <h3>Submitted Text</h3>
          <div className="text-content">
            {submission.content}
          </div>
        </div>
      )}

      {submission.fileUrls && submission.fileUrls.length > 0 && (
        <div className="file-submissions">
          <h3>Submitted Files</h3>

          <div className="file-tabs">
            {submission.fileUrls.map((file, index) => (
              <button
                key={index}
                className={`file-tab ${selectedFile === file ? 'active' : ''}`}
                onClick={() => setSelectedFile(file)}
              >
                {file.name}
              </button>
            ))}
          </div>

          <div className="file-preview">
            {selectedFile ? renderFileContent(selectedFile) : (
              <div className="no-file-selected">
                Select a file to preview
              </div>
            )}
          </div>
        </div>
      )}

      {!submission.content && (!submission.fileUrls || submission.fileUrls.length === 0) && (
        <div className="empty-submission">
          This submission does not contain any content or files.
        </div>
      )}
    </div>
  );
};

export default SubmissionViewer;